import React, { useState, useEffect } from "react";
import { useHotel } from "../contexts/HotelContext";
import { getAvailablePermissions } from "../services/apiKeysApi";
import Layout from "../components/Layout";

interface EndpointDoc {
  permission: string;
  method: string;
  path: string;
  description: string;
  example?: string;
}

const ENDPOINTS: EndpointDoc[] = [
  {
    permission: "grants:schedule",
    method: "GET",
    path: "/grants/kfs/articles/list",
    description: "Lista artykulow KFS z harmonogramem naborow",
    example: `curl -X GET "https://api.hotelb2b.pl/grants/kfs/articles/list?page=1&limit=10" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "X-Tenant-Id: YOUR_TENANT_ID"`,
  },
  {
    permission: "grants:schedule",
    method: "GET",
    path: "/grants/kfs/grants",
    description: "Lista dotacji KFS",
    example: `curl -X GET "https://api.hotelb2b.pl/grants/kfs/grants?page=1&limit=10" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "X-Tenant-Id: YOUR_TENANT_ID"`,
  },
  {
    permission: "hotelb2b:offers:read",
    method: "GET",
    path: "/offers",
    description: "Lista ofert hotelu",
    example: `curl -X GET "https://api.hotelb2b.pl/offers" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "X-Tenant-Id: YOUR_TENANT_ID" \\
  -H "X-Hotel-Id: YOUR_HOTEL_ID"`,
  },
  {
    permission: "hotelb2b:offers:write",
    method: "POST",
    path: "/offers",
    description: "Tworzenie nowej oferty",
    example: `curl -X POST "https://api.hotelb2b.pl/offers" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "X-Tenant-Id: YOUR_TENANT_ID" \\
  -H "X-Hotel-Id: YOUR_HOTEL_ID" \\
  -H "Content-Type: application/json" \\
  -d '{"title": "Nowa oferta", ...}'`,
  },
];

const ApiDocumentation: React.FC = () => {
  const { currentTenant } = useHotel();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const tenantId = currentTenant?.id;

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!tenantId) {
        setLoading(false);
        return;
      }

      try {
        const { permissions } = await getAvailablePermissions(tenantId);
        setAvailablePermissions(permissions);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [tenantId]);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Check if user has permission (supports wildcards)
  const hasPermission = (required: string): boolean => {
    return availablePermissions.some((perm) => {
      if (perm === "*") return true;
      if (perm === required) return true;

      // Handle wildcards like "grants:*" matching "grants:schedule"
      const permParts = perm.split(":");
      const reqParts = required.split(":");

      for (let i = 0; i < permParts.length; i++) {
        if (permParts[i] === "*") return true;
        if (permParts[i] !== reqParts[i]) return false;
      }

      return permParts.length <= reqParts.length;
    });
  };

  const availableEndpoints = ENDPOINTS.filter((ep) =>
    hasPermission(ep.permission)
  );

  const CodeBlock: React.FC<{ code: string; id: string }> = ({ code, id }) => (
    <div className="relative group">
      <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 px-2 py-1 text-xs bg-neutral-700 hover:bg-neutral-600 text-neutral-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copiedCode === id ? "Skopiowano!" : "Kopiuj"}
      </button>
    </div>
  );

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Ladowanie...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      <div className="max-w-4xl">
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200 text-left">
          <div className="px-6 py-8 border-b border-neutral-200">
            <h1 className="text-2xl font-bold text-neutral-900">
              Dokumentacja API
            </h1>
            <p className="mt-2 text-neutral-600">
              Dostepne endpointy dla organizacji {currentTenant?.name || ""}
            </p>
            <div className="mt-3 text-sm text-neutral-500">
              Tenant ID:{" "}
              <code className="bg-neutral-100 px-2 py-1 rounded font-mono">
                {tenantId}
              </code>
            </div>
          </div>

          <div className="px-6 py-8 space-y-8">
            {/* Authentication Headers */}
            <section>
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Naglowki autentykacji
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200 border border-neutral-200 rounded-lg">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Naglowek
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Wymagany
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Opis
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    <tr>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <code className="text-sm bg-neutral-100 px-2 py-1 rounded">
                          X-API-Key
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-green-600 font-semibold">Tak</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        Twoj klucz API
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <code className="text-sm bg-neutral-100 px-2 py-1 rounded">
                          X-Tenant-Id
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-green-600 font-semibold">Tak</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        {tenantId}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <code className="text-sm bg-neutral-100 px-2 py-1 rounded">
                          X-Hotel-Id
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-neutral-500">Opcjonalnie</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        UUID hotelu (dla endpointow hotel-level)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Available Endpoints */}
            <section>
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Dostepne endpointy ({availableEndpoints.length})
              </h2>

              {availableEndpoints.length === 0 ? (
                <div className="text-center py-8 text-neutral-500">
                  <p>Brak dostepnych endpointow dla Twojego konta.</p>
                  <p className="text-sm mt-2">
                    Skontaktuj sie z administratorem, aby uzyskac dostep.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {availableEndpoints.map((endpoint, index) => (
                    <div
                      key={index}
                      className="border border-neutral-200 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`px-2 py-1 text-xs font-bold rounded ${
                            endpoint.method === "GET"
                              ? "bg-green-100 text-green-700"
                              : endpoint.method === "POST"
                                ? "bg-blue-100 text-blue-700"
                                : endpoint.method === "PUT"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700"
                          }`}
                        >
                          {endpoint.method}
                        </span>
                        <code className="text-sm font-mono text-neutral-800">
                          {endpoint.path}
                        </code>
                      </div>
                      <p className="text-sm text-neutral-600 mb-3">
                        {endpoint.description}
                      </p>
                      <div className="text-xs text-neutral-500 mb-2">
                        Uprawnienie:{" "}
                        <code className="bg-neutral-100 px-1 rounded">
                          {endpoint.permission}
                        </code>
                      </div>
                      {endpoint.example && (
                        <CodeBlock
                          code={endpoint.example}
                          id={`endpoint-${index}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Error Responses */}
            <section>
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Odpowiedzi bledow
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-neutral-800 mb-2">
                    401 Unauthorized
                  </h3>
                  <CodeBlock
                    id="error-401"
                    code={`{
  "statusCode": 401,
  "message": "X-Tenant-Id header is required",
  "error": "Unauthorized"
}`}
                  />
                </div>

                <div>
                  <h3 className="text-lg font-medium text-neutral-800 mb-2">
                    403 Forbidden
                  </h3>
                  <CodeBlock
                    id="error-403"
                    code={`{
  "statusCode": 403,
  "message": "API key does not have required permission",
  "error": "Forbidden"
}`}
                  />
                </div>
              </div>
            </section>

            {/* Back Link */}
            <div className="pt-8 border-t border-neutral-200">
              <a
                href="/tenant/api-keys"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                &larr; Powrot do zarzadzania kluczami API
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ApiDocumentation;
